import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, MapPin, Loader2, CheckCircle2, MessageCircle, Facebook, Instagram } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { SITE } from "@/constants/site";

function TikTokIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" {...props}>
      <path d="M21 8.5a7.5 7.5 0 0 1-4.6-1.6v8.6A6.5 6.5 0 1 1 9.9 9V12a3.5 3.5 0 1 0 2.5 3.3V2h3a4.5 4.5 0 0 0 4.5 4.5v2z" />
    </svg>
  );
}

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await apiClient.post("/contact", form);
      setSent(true);
      toast.success("Message sent! We'll reply soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="eyebrow text-[#1E3A8A]">Get in touch</div>
        <h1 className="font-display heading-uppercase text-5xl sm:text-6xl leading-[0.95] mt-3">
          Let's <span className="text-[#1E3A8A]">talk football.</span>
        </h1>
        <p className="mt-6 text-slate-700 text-lg max-w-2xl">Questions about a camp? Want to discuss a private programme? Send us a message and we'll get back to you fast.</p>

        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 flex items-start gap-4">
              <Mail className="w-5 h-5 text-[#1E3A8A] mt-1" />
              <div>
                <div className="eyebrow text-slate-500">Email</div>
                <a href={`mailto:${SITE.email}`} className="text-slate-900 hover:text-[#1E3A8A]">{SITE.email}</a>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 flex items-start gap-4">
              <Phone className="w-5 h-5 text-[#1E3A8A] mt-1" />
              <div>
                <div className="eyebrow text-slate-500">Phone</div>
                <a href={`tel:${SITE.phone}`} className="text-slate-900 hover:text-[#1E3A8A]">{SITE.phone}</a>
              </div>
            </div>
            <a href={`https://wa.me/${SITE.whatsapp}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="block rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6 hover:border-emerald-400/60 transition">
              <div className="flex items-center gap-4">
                <MessageCircle className="w-5 h-5 text-emerald-300" />
                <div>
                  <div className="eyebrow text-emerald-300">WhatsApp</div>
                  <div className="text-slate-900">Tap to chat instantly</div>
                </div>
              </div>
            </a>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 flex items-start gap-4">
              <MapPin className="w-5 h-5 text-[#1E3A8A] mt-1" />
              <div>
                <div className="eyebrow text-slate-500">Location</div>
                <div className="text-slate-900">{SITE.address}</div>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <div className="eyebrow text-slate-500 mb-3">Follow us</div>
              <div className="flex gap-3">
                <a href={SITE.socials.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="w-10 h-10 grid place-items-center rounded-full border border-slate-200 hover:bg-[#2563EB] hover:border-[#2563EB] transition"><Facebook className="w-4 h-4" /></a>
                <a href={SITE.socials.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="w-10 h-10 grid place-items-center rounded-full border border-slate-200 hover:bg-[#FACC15] hover:text-[#020617] hover:border-[#FACC15] transition"><Instagram className="w-4 h-4" /></a>
                <a href={SITE.socials.tiktok} target="_blank" rel="noreferrer" aria-label="TikTok" className="w-10 h-10 grid place-items-center rounded-full border border-slate-200 hover:bg-white hover:text-[#020617] hover:border-white transition"><TikTokIcon /></a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8">
              {sent ? (
                <div className="py-10 flex flex-col items-center text-center gap-4">
                  <CheckCircle2 className="w-16 h-16 text-emerald-400" />
                  <h3 className="font-display uppercase text-2xl">Message sent</h3>
                  <p className="text-slate-500 max-w-md">Thanks for reaching out — we'll get back to you within 24 hours.</p>
                  <button data-testid="contact-send-another" onClick={() => setSent(false)} className="btn-gold text-sm">Send another</button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="eyebrow text-slate-500">Name *</Label>
                      <Input data-testid="contact-name" required value={form.name} onChange={(e) => update("name", e.target.value)} className="mt-1 bg-white border-slate-200" />
                    </div>
                    <div>
                      <Label className="eyebrow text-slate-500">Email *</Label>
                      <Input data-testid="contact-email" required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1 bg-white border-slate-200" />
                    </div>
                    <div>
                      <Label className="eyebrow text-slate-500">Phone</Label>
                      <Input data-testid="contact-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-1 bg-white border-slate-200" />
                    </div>
                    <div>
                      <Label className="eyebrow text-slate-500">Subject</Label>
                      <Input data-testid="contact-subject" value={form.subject} onChange={(e) => update("subject", e.target.value)} className="mt-1 bg-white border-slate-200" />
                    </div>
                  </div>
                  <div>
                    <Label className="eyebrow text-slate-500">Message *</Label>
                    <Textarea data-testid="contact-message" required rows={6} value={form.message} onChange={(e) => update("message", e.target.value)} className="mt-1 bg-white border-slate-200" />
                  </div>
                  <div className="flex justify-end">
                    <button data-testid="contact-submit-btn" disabled={loading} type="submit" className="btn-gold">
                      {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : "Send Message"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
